import { useMemo } from 'react';

import { useGetStream } from '@vlabs/api/luna-api/client';
import type { StreamDataBaseWithoutDefaultType } from '@vlabs/api/luna-api/model';
import { getTimeOffset } from '@vlabs/shared/utils';
import { parseISOToLocale } from '@vlabs/shared/utils/date';

const emptyTimeOffset = '-';

interface UseStreamTimeOffsetResult {
  sourceType?: StreamDataBaseWithoutDefaultType;
  timeInterval?: string;
}

export function useStreamTimeOffset(
  streamId?: string,
  timeOffset?: number,
): UseStreamTimeOffsetResult {
  const canLoadStream = !!streamId && timeOffset != null;
  const { data: stream, isError } = useGetStream(streamId ?? '', undefined, {
    query: { enabled: canLoadStream },
  });

  return useMemo(() => {
    if (!canLoadStream) return {};
    if (isError) return { timeInterval: emptyTimeOffset };
    if (!stream) return {};

    const sourceType = stream.data?.type;
    const timestampSource = stream.data?.timestamp_source;
    const startTime = stream.data?.pts?.start_time;

    if (startTime == null || !sourceType) {
      return { sourceType, timeInterval: emptyTimeOffset };
    }

    if (
      startTime > 0
      || timestampSource === 'server'
      || (startTime === 0 && timestampSource === 'auto' && sourceType !== 'videofile')
    ) {
      const isoDate = new Date(timeOffset * 1000).toISOString();
      const formattedTime = parseISOToLocale(isoDate);

      return {
        sourceType,
        timeInterval: formattedTime?.length ? formattedTime : emptyTimeOffset,
      };
    }

    if (sourceType === 'stream' || sourceType === 'videofile') {
      const formattedOffset = getTimeOffset(timeOffset, startTime);

      return {
        sourceType,
        timeInterval: formattedOffset?.length ? formattedOffset : emptyTimeOffset,
      };
    }

    return { sourceType, timeInterval: emptyTimeOffset };
  }, [canLoadStream, isError, stream, timeOffset]);
}
